import { useEffect, useState } from 'react';
import type { ProductIntroApi } from '@/api/product-intro';
import type { ProductIntroMatch, ProductIntroNoMatch } from '@/types/product-intro';
import type { Locale } from '@/types/widget';

export type ProductIntroState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'match'; result: ProductIntroMatch }
  | { status: 'no-match'; result: ProductIntroNoMatch }
  | { status: 'error'; error: Error };

/**
 * Manage the product-intro fetch state machine. Re-fetches whenever
 * `query` changes (caller is expected to debounce upstream via
 * `useDebounce`). Aborts the previous in-flight request on change.
 *
 * Empty / whitespace-only queries stay in `idle` without calling the API.
 */
export function useProductIntro(
  api: ProductIntroApi,
  query: string,
  locale: Locale,
): ProductIntroState {
  const [state, setState] = useState<ProductIntroState>({ status: 'idle' });

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length === 0) {
      setState({ status: 'idle' });
      return;
    }

    const ctrl = new AbortController();

    setState({ status: 'loading' });

    (async () => {
      try {
        const result = await api.search(
          { query: trimmed, language: locale },
          { signal: ctrl.signal },
        );
        if (ctrl.signal.aborted) return;
        if (result.match) {
          setState({ status: 'match', result });
        } else {
          setState({ status: 'no-match', result });
        }
      } catch (err) {
        if (ctrl.signal.aborted) return;
        setState({ status: 'error', error: err as Error });
      }
    })();

    return () => {
      ctrl.abort();
    };
  }, [api, query, locale]);

  return state;
}
